import { createTheme } from '@mui/material';

export const FontArray = ['Pangram', 'Lato', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(',');

let theme = createTheme({
	components: {
		MuiSnackbar: {
			styleOverrides: {
				root: {
					width: '1200px',
				},
			},
		},
		MuiButton: {
			variants: [
				{
					props: { variant: 'contrast' },
					style: {
						color: '#2C3340',
						backgroundColor: 'transparent',
						textDecoration: 'underline',
						':hover': {
							opacity: 0.6,
							textDecoration: 'underline',
						},
					},
				},
			],
		},
	},
	typography: {
		fontFamily: FontArray,
		// Non standard headings - to be brought in line for the design system
		'dashboard-card-heading': {
			fontSize: 24,
			fontWeight: 600,
			lineHeight: '32px',
			color: '#005A7A',
		},
	},
	palette: {
		primary: {
			light: '#F2F4F7;',
			main: '#2C3340',
			dark: '#0F1216',
		},
		secondary: {
			light: '#E6F4F8',
			main: '#005A7A',
			dark: '#001C27',
		},
		navigationButtonColor: {
			main: '#FFF',
		},
		regNumberColor: {
			main: '#353a6e',
		},
		annualMileageFont: {
			main: '#1F2241',
		},
		contrastThreshold: 3,
		tonalOffset: 0.2,
		background: {
			default: '#ebeefe',
		},
		footer: {
			main: '#2C3340',
			dark: '#0F1216',
		},
		icon: {
			main: '#005A7A',
		},
	},
});

theme = createTheme(theme, {
	components: {
		MuiTableCell: {
			styleOverrides: {
				root: {
					fontSize: '0.875rem',
				},
			},
		},
		MuiTypography: {
			defaultProps: {
				variantMapping: {
					'dashboard-card-heading': 'h2',
				},
			},
			styleOverrides: {
				root: {
					'&& a': {
						color: theme.palette.secondary.main,
						textDecoration: 'underline',
						'&:hover': {
							color: theme.palette.primary.dark,
							opacity: 0.9,
							textDecoration: 'underline',
						},
					},
					'&&  p > a': {
						color: theme.palette.secondary.main,
						textDecoration: 'underline',
						'&:hover': {
							color: theme.palette.primary.dark,
							opacity: 0.9,
							textDecoration: 'underline',
						},
					},
					'& .MuiTypography-h6 h6': {
						fontWeight: 300,
					},
				},
				h1: {
					color: theme.palette.primary.main,
					fontWeight: 400,
				},
				h2: {
					color: theme.palette.primary.main,
					fontWeight: 400,
					fontSize: '3.5rem',
				},
				h4: {
					color: theme.palette.primary.main,
					fontWeight: 500,
				},
				h5: {
					color: theme.palette.primary.main,
					fontWeight: 600,
				},
				h6: {
					color: theme.palette.primary.main,
					fontWeight: 400,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: ({ ownerState }: { ownerState: { color: string; variant: string; size: string; id: string } }) => ({
					...(ownerState.variant !== 'text' &&
						ownerState.variant !== 'outlined' &&
						ownerState.variant !== 'contrast' && {
							...(ownerState.color === 'secondary' && {
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.common.white,
								':hover': {
									opacity: 0.6,
								},
							}),
							...(ownerState.color === 'primary' && {
								backgroundColor: theme.palette.secondary.main,
								color: theme.palette.common.white,
								':hover': {
									opacity: 0.6,
								},
							}),
							...(ownerState.size === 'large' &&
								ownerState.color === 'primary' && {
									backgroundColor: theme.palette.primary.main,
									color: theme.palette.common.white,
									':hover': {
										opacity: 0.6,
									},
								}),
						}),
				}),
			},
			variants: [
				{
					props: { variant: 'link' },
					style: {
						...theme.typography.body1,
						color: theme.palette.secondary.main,
						textDecoration: 'underline',
						padding: 0,
						background: 'transparent',
						'&:hover': {
							color: theme.palette.primary.main,
							background: 'transparent',
						},
						'&:visited': {
							background: 'transparent',
							color: theme.palette.secondary.main,
							'&:hover': {
								color: theme.palette.secondary.light,
							},
						},
					},
				},
			],
		},
		MuiLink: {
			styleOverrides: {
				root: {
					color: theme.palette.primary.main,
					textDecoration: 'underline',
					'&:hover': {
						color: theme.palette.primary.dark,
						textDecoration: 'underline',
					},
				},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					color: theme.palette.icon.main,
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					color: theme.palette.primary.main,
					'&.Mui-selected': {
						backgroundColor: theme.palette.secondary.light,
						color: theme.palette.secondary.main,
						'&:hover': {
							backgroundColor: theme.palette.secondary.light,
						},
					},
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					backgroundColor: theme.palette.primary.main,
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					color: theme.palette.common.black,
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderColor: theme.palette.common.black,
				},
				notchedOutline: {
					borderColor: theme.palette.common.black,
				},
			},
		},
		MuiCssBaseline: {
			styleOverrides: `
        body, html, #root {
          height: 100%;
		  background-color: ${theme.palette.primary.light};
        }
        html {
          overflow-y: scroll;
        }
        /* ===== Scrollbar CSS ===== */
        /* Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: ${theme.palette.grey[500]} ${theme.palette.background.default};
        }

        /* Chrome, Edge, and Safari */
        *::-webkit-scrollbar {
          width: 12px;
        }

        *::-webkit-scrollbar-track {
          background: ${theme.palette.primary.light};
        }

        *::-webkit-scrollbar-thumb {
          background-color: ${theme.palette.grey[500]};
          border-radius: 10px;
          border: 3px solid ${theme.palette.background.default};
        }
		.MuiTypography-root a {
      color: ${theme.palette.secondary.main};
      text-decoration: underline;
    }
    .MuiTypography-root a:hover {
      color: ${theme.palette.primary.dark};
      opacity: 0.9;
    }`,
		},
	},
});

const themeExport = theme;
export default themeExport;
